import { useId } from 'react';

/** The colours of a miniature Bridge app window in one theme: mostly the `global.css` tokens; the text bars use softer greys of their own. */
interface Palette {
  readonly card: string;
  readonly cardLine: string;
  readonly heading: string;
  readonly text: string;
  readonly tile: string;
  readonly tileLine: string;
  readonly iconTile: string;
  readonly icon: string;
  readonly button: string;
  readonly guide: string;
  readonly guideMark: string;
  readonly guideTitle: string;
  readonly guideText: string;
}

const LIGHT: Palette = {
  card: '#ffffff',
  cardLine: '#e5e5ea',
  heading: '#000000',
  text: '#a8a8ad',
  tile: '#f7f7fa',
  tileLine: '#e5e5ea',
  iconTile: '#dde9fd',
  icon: '#2563eb',
  button: '#000000',
  guide: '#f7f7fa',
  guideMark: '#d1d1d6',
  guideTitle: '#3c3c43',
  guideText: '#a8a8ad',
};

const DARK: Palette = {
  card: '#232528',
  cardLine: '#36373a',
  heading: '#ffffff',
  text: '#6b6e74',
  tile: '#2a2c2f',
  tileLine: '#36373a',
  iconTile: '#2b3a56',
  icon: '#60a5fa',
  button: '#2563eb',
  guide: '#2d2e30',
  guideMark: '#55575c',
  guideTitle: '#e4e5e7',
  guideText: '#7d8086',
};

/** The step guide's marks: done in green, the current one in blue. */
const MARK = { done: '#34c759', current: '#3b82f6' };

function Window({ c }: { readonly c: Palette }): React.JSX.Element {
  return (
    <g>
      {/* just the app window, edge to edge: the tool on the left, the guide on the right */}
      <rect x="0.5" y="0.5" width="151" height="95" rx="7.5" fill={c.card} stroke={c.cardLine} />
      <rect x="10" y="11" width="50" height="5" rx="2.5" fill={c.heading} />
      <rect x="10" y="20" width="58" height="3" rx="1.5" fill={c.text} />
      {[10, 41, 72].map((x) => (
        <g key={x}>
          <rect x={x + 0.5} y="29.5" width="27" height="27" rx="4" fill={c.tile} stroke={c.tileLine} />
          <rect x={x + 5} y="34" width="8" height="8" rx="2" fill={c.iconTile} />
          <rect x={x + 7} y="36" width="4" height="4" rx="1" fill={c.icon} />
          <rect x={x + 5} y="47" width="15" height="3" rx="1.5" fill={c.heading} />
        </g>
      ))}
      <rect x="10" y="67" width="38" height="12" rx="3" fill={c.button} />
      <rect x="18" y="71.75" width="22" height="2.5" rx="1.25" fill="#ffffff" />
      <path d="M110 1h34.5a6.5 6.5 0 0 1 6.5 6.5v81a6.5 6.5 0 0 1-6.5 6.5H110z" fill={c.guide} />
      {[0, 1, 2, 3].map((i) => (
        <g key={i}>
          {i < 3 ? <rect x="117.4" y={18 + i * 19} width="1.2" height="10" fill={i === 0 ? MARK.done : c.guideMark} /> : null}
          <circle cx="118" cy={14 + i * 19} r="3.2" fill={i === 0 ? MARK.done : c.guide} stroke={i === 0 ? MARK.done : i === 1 ? MARK.current : c.guideMark} strokeWidth="1" />
          <rect x="125" y={11.5 + i * 19} width={i % 2 === 0 ? 18 : 15} height="3" rx="1.5" fill={c.guideTitle} />
          <rect x="125" y={17 + i * 19} width={i % 2 === 0 ? 20 : 22} height="2.4" rx="1.2" fill={c.guideText} />
        </g>
      ))}
    </g>
  );
}

/**
 * A miniature of the Bridge app window in a theme, for the appearance switch
 * (since 0.22.2 only the window itself: no page, no header): light, dark, or
 * for “System” both, side by side: light on the left, dark on the right, split
 * on a straight vertical line through the gap between the second and the third
 * tile, so no shape is cut in two.
 */
export function ThemePreview({ kind }: { readonly kind: 'auto' | 'light' | 'dark' }): React.JSX.Element {
  const clip = `theme-split-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  return (
    <svg width="112" height="71" viewBox="0 0 152 96" aria-hidden="true" focusable="false" style={{ display: 'block', borderRadius: 6 }}>
      {kind === 'dark' ? <Window c={DARK} /> : <Window c={LIGHT} />}
      {kind === 'auto' ? (
        <>
          <defs>
            <clipPath id={clip}>
              <rect x="70.5" y="0" width="81.5" height="96" />
            </clipPath>
          </defs>
          <g clipPath={`url(#${clip})`}>
            <Window c={DARK} />
          </g>
        </>
      ) : null}
    </svg>
  );
}
